import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { BASE_URL, ROUTES } from "../assets/constant";
import { loggedIn } from "../redux/authSlice";
import recalllogo from "../assets/images/recalllogo.png";
import secureImage from "../assets/images/img1.jpg";
import "./otp-verification.css";

const OTPVerification = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get email from Redux (set in login page)
    const email = useSelector((state) => state.auth?.otpEmail);

    // State
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const inputRefs = useRef([]);

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        // Allow only numbers
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(0, 1);
        setOtp(newOtp);

        // Clear error when user starts typing
        if (error) setError("");

        // Auto focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text");
        const digits = pastedData.slice(0, 6).match(/\d/g);

        if (digits) {
            const newOtp = [...otp];
            digits.forEach((digit, idx) => {
                if (idx < 6) newOtp[idx] = digit;
            });
            setOtp(newOtp);
            inputRefs.current[Math.min(digits.length, 5)]?.focus();
        }
    };

    const getRealSubscriptions = async (userId, token) => {
        try {
            const response = await axios.post(
                `${BASE_URL}/recall/v1/get-subscriptions`,
                { user_id: userId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data.subscriptions || [];
        } catch (error) {
            console.error("Error fetching subscriptions:", error);
            return [];
        }
    };

    // Resend OTP
    const handleResendOTP = async () => {
        if (!email) {
            setError("Session expired. Please login again");
            return;
        }

        setIsResending(true);
        setError("");
        setSuccessMessage("");

        try {
            const response = await axios.post(`${BASE_URL}/users/resend-otp`, {
                email: email
            });

            if (response.data.success) {
                // Reset OTP inputs
                setOtp(["", "", "", "", "", ""]);

                // Show success message
                setSuccessMessage(response.data.message || "New OTP sent successfully");

                // Focus first input
                setTimeout(() => {
                    inputRefs.current[0]?.focus();
                }, 100);

                // Clear success message after 5 seconds
                setTimeout(() => {
                    setSuccessMessage("");
                }, 5000);
            } else {
                setError("Failed to resend OTP. Please try again.");
            }
        } catch (error) {
            console.error("Resend OTP failed:", error);
            setError(
                error?.response?.data?.message || "Failed to resend OTP. Please try again."
            );
        } finally {
            setIsResending(false);
        }
    };

    // Verify OTP
    const handleVerifyOTP = async () => {
        const otpValue = otp.join("");

        if (otpValue.length !== 6) {
            setError("Please enter 6-digit code");
            return;
        }

        if (!email) {
            setError("Session expired. Please login again");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccessMessage("");

        try {
            const response = await axios.post(
                `${BASE_URL}/users/verify-otp`,
                {
                    email,
                    otp: otpValue,
                }
            );
            console.log("VERIFY OTP RESPONSE", response.data);
            if (response.data.success) {
                const token = response.data.token;
                const userId = response.data.user.id;

                const realSubscriptions = await getRealSubscriptions(
                    userId,
                    token
                );

                dispatch(
                    loggedIn({
                        isAuthenticated: true,
                        user: response.data.user,
                        token: token,
                        subscription: realSubscriptions,
                    })
                );

                if (realSubscriptions.length > 0) {
                    navigate(ROUTES.DASHBOARD, { replace: true });
                } else {
                    navigate(ROUTES.MANAGE_SUBSCRIPTION, { replace: true });
                }
            } else {
                setError("Invalid OTP. Please try again.");
                setOtp(["", "", "", "", "", ""]);
                inputRefs.current[0]?.focus();
            }
        } catch (error) {
            console.error("OTP verification failed:", error);

            setError(
                error?.response?.data?.message ||
                "Verification failed. Please try again."
            );

            setOtp(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        } finally {
            setIsLoading(false);
        }
    };

    // Get masked email for display
    const getMaskedEmail = () => {
        if (!email) return "pri****@example.com";
        const [username, domain] = email.split("@");
        const maskedUsername = username.slice(0, 3) + "****";
        return `${maskedUsername}@${domain}`;
    };

    return (
        <div className="otp-page">
            <header className="otp-header">
                <img src={recalllogo} alt="Recall Alarms" className="otp-logo" />
                <div className="otp-help">
                    <i className="fa-regular fa-circle-question"></i>
                    <span>Need help?</span>
                </div>
            </header>

            <div className="otp-content">
                {/* Left Section */}
                <div className="otp-left">
                    <div className="otp-illustration">
                        <img src={secureImage} alt="Secure Verification" />
                    </div>
                    <h2>Secure Verification</h2>
                    <p>
                        Enter the 6-digit verification code
                        <br />
                        sent to your registered email address
                        <br />
                        to continue.
                    </p>
                </div>

                {/* Right Section - OTP Card */}
                <div className="otp-card">
                    <div className="otp-icon">
                        <i className="fa-solid fa-shield-halved"></i>
                    </div>

                    <h1>OTP Verification</h1>

                    <p className="otp-description">
                        We've sent a 6-digit verification code to
                    </p>

                    <div className="otp-email-row">
                        <span className="otp-email">{getMaskedEmail()}</span>
                    </div>

                    <div className="otp-label">Enter 6-digit code</div>

                    <div className="otp-input-wrapper">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                maxLength="1"
                                className="otp-input"
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                disabled={isLoading}
                                autoFocus={index === 0}
                            />
                        ))}
                    </div>

                    {/* Success Message */}
                    {successMessage && (
                        <p className="text-success" style={{ textAlign: "center", marginTop: "10px", marginBottom: "0px" }}>
                            {successMessage}
                        </p>
                    )}

                    {/* Error Message */}
                    {error && (
                        <p className="text-danger" style={{ textAlign: "center", marginTop: "10px", marginBottom: "0px" }}>
                            {error}
                        </p>
                    )}

                    <button
                        className="verify-btn"
                        onClick={handleVerifyOTP}
                        disabled={isLoading}
                    >
                        {isLoading ? "Verifying..." : "Verify OTP"}
                    </button>

                    <div className="resend-wrapper">
                        Didn't receive the code?
                        <button
                            className="resend-btn"
                            onClick={handleResendOTP}
                            disabled={isResending}
                        >
                            {isResending ? "Sending..." : "Resend OTP"}
                        </button>
                    </div>

                    <div className="divider">
                        <span>or</span>
                    </div>

                    <button className="back-btn" onClick={() => navigate("/login")}>
                        ← Back to Login
                    </button>
                </div>
            </div>

            <footer className="otp-footer">
                ©2026 Recall Alarms. All rights reserved.
            </footer>
        </div>
    );
};

export default OTPVerification;